import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const ShareSessionControl = ({
  sessionId = 'demo-session-123',
  sessionUrl = '',
  qrCodeUrl = '',
  onGenerateQR = () => { },
  onCopyLink = () => { },
  onShareWhatsApp = () => { },
  onShareEmail = () => { },
  position = 'floating' // 'floating' | 'inline'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Prefer explicit production base URL (set at build time via VITE_BASE_URL),
  // otherwise use the runtime origin. This avoids generating localhost links
  // when the app is running from file:// or an embedded WebView with a dev origin.
  const envBase = import.meta.env.VITE_BASE_URL || '';
  const runtimeOrigin = (window?.location?.origin) || '';
  // If the runtime origin is localhost (Capacitor local server) or empty, prefer envBase
  const baseOrigin = (runtimeOrigin.includes('localhost') || !runtimeOrigin)
    ? (envBase || 'https://splitbill.logicmindstudio.com')
    : runtimeOrigin.replace(/\/$/, '');
  const defaultSessionUrl = sessionUrl || `${baseOrigin}/join/${sessionId}`;

  // Debugging aid: log the computed origin/url in development so we can confirm
  // on device/emulator via logcat that links won't point to localhost.
  if (import.meta.env.DEV) {
    console.debug(`[ShareSession] baseOrigin=${baseOrigin} defaultSessionUrl=${defaultSessionUrl}`);
  }
  const defaultQRUrl = qrCodeUrl || `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(defaultSessionUrl)}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard?.writeText(defaultSessionUrl);
      setCopySuccess(true);
      onCopyLink(defaultSessionUrl);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleShareWhatsApp = () => {
    const message = encodeURIComponent(`¡Únete a nuestra cuenta compartida! ${defaultSessionUrl}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
    onShareWhatsApp(defaultSessionUrl);
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent('Invitación a cuenta compartida - SplitBill');
    const body = encodeURIComponent(`Hola,\n\nTe invito a unirte a nuestra cuenta compartida en SplitBill.\n\nHaz clic en el siguiente enlace para unirte:\n${defaultSessionUrl}\n\n¡Nos vemos pronto!`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    onShareEmail(defaultSessionUrl);
  };

  const handleGenerateQR = () => {
    onGenerateQR(sessionId);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (position === 'floating') {
    return (
      <>
        {/* Floating Action Button */}
        <div className="fixed bottom-6 right-6 z-40">
          <Button
            variant="default"
            size="lg"
            onClick={toggleExpanded}
            className="rounded-full shadow-elevation-3 hover:shadow-elevation-3 transition-all duration-300"
            iconName="Share2"
            iconSize={20}
          >
            <span className="hidden sm:inline ml-2">Compartir</span>
          </Button>
        </div>

        {/* Expanded Share Menu */}
        {isExpanded && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/20 z-30"
              onClick={toggleExpanded}
            />

            {/* Share Panel */}
            <div className="fixed bottom-24 right-6 z-40 bg-surface border border-border rounded-lg shadow-elevation-3 p-4 w-80 max-w-[calc(100vw-3rem)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground">Compartir Sesión</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleExpanded}
                  className="h-8 w-8"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>

              {/* QR Code */}
              <div className="text-center mb-4">
                <div className="inline-block p-3 bg-white rounded-lg border border-border">
                  <img
                    src={defaultQRUrl}
                    alt="QR Code para unirse a la sesión"
                    className="w-24 h-24"
                    onError={(e) => {
                      e.target.src = '/assets/images/no_image.png';
                    }}
                  />
                </div>
                <p className="text-xs text-text-secondary mt-2">
                  Escanea para unirte
                </p>
              </div>

              {/* Share Options */}
              <div className="space-y-2">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={handleCopyLink}
                  iconName={copySuccess ? "Check" : "Copy"}
                  iconPosition="left"
                  iconSize={16}
                  className={copySuccess ? "text-success border-success" : ""}
                >
                  {copySuccess ? "¡Enlace copiado!" : "Copiar enlace"}
                </Button>

                <Button
                  variant="outline"
                  fullWidth
                  onClick={handleShareWhatsApp}
                  iconName="MessageCircle"
                  iconPosition="left"
                  iconSize={16}
                >
                  Compartir por WhatsApp
                </Button>

                <Button
                  variant="outline"
                  fullWidth
                  onClick={handleShareEmail}
                  iconName="Mail"
                  iconPosition="left"
                  iconSize={16}
                >
                  Compartir por email
                </Button>
              </div>

              {/* Session Info */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="text-xs text-text-secondary">
                  <div className="flex items-center justify-between">
                    <span>ID de sesión:</span>
                    <span className="font-mono">{sessionId}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // Inline version
  return (
    <div className="bg-surface border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground">Compartir Sesión</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleGenerateQR}
          iconName="RefreshCw"
          iconSize={16}
        >
          Actualizar
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {/* QR Code Section */}
        <div className="text-center">
          <div className="inline-block p-4 bg-white rounded-lg border border-border mb-3">
            <img
              src={defaultQRUrl}
              alt="QR Code para unirse a la sesión"
              className="w-32 h-32"
              onError={(e) => {
                e.target.src = '/assets/images/no_image.png';
              }}
            />
          </div>
          <p className="text-sm text-text-secondary">
            Escanea el código QR para unirte
          </p>
        </div>

        {/* Share Options Section */}
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Enlace de la sesión
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={defaultSessionUrl}
                readOnly
                className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-muted text-text-secondary"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                iconName={copySuccess ? "Check" : "Copy"}
                iconSize={16}
                className={copySuccess ? "text-success border-success" : ""}
              >
                {copySuccess ? "✓" : ""}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Button
              variant="outline"
              fullWidth
              onClick={handleShareWhatsApp}
              iconName="MessageCircle"
              iconPosition="left"
              iconSize={16}
            >
              WhatsApp
            </Button>

            <Button
              variant="outline"
              fullWidth
              onClick={handleShareEmail}
              iconName="Mail"
              iconPosition="left"
              iconSize={16}
            >
              Email
            </Button>
          </div>
        </div>
      </div>
      {/* Session Details */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-text-secondary">ID de sesión:</span>
            <div className="font-mono text-foreground">{sessionId}</div>
          </div>
          <div>
            <span className="text-text-secondary">Creada:</span>
            <div className="text-foreground">
              {new Date()?.toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareSessionControl;